import React, { useEffect, useState } from 'react';
import axios from 'axios';
import EpicSelector from '../components/EpicSelector';
import IssuesList from '../components/IssuesList';
import TasksList from '../components/TasksList';
import '../App.css';
import { Alert, Spinner, Modal, Button } from 'react-bootstrap';


interface Epic {
  id: number;
  key: string;
  summary: string;
}

interface Project {
  url: string;
  name: string;
  id: number;
  jiraKey: string;
}

interface Task {
  url: string;
  project: string;
  name: string;
  isBillable: boolean;
  billingRate: string;
  billingPeriod: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  jiraKey: string;
}

interface Issue {
  id: number;
  key: string;
  summary: string;
  epic: string;
}

const JiraFreeAgentSyncPage: React.FC = () => {

  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;

  const authHeader = `Basic ${btoa(`${username}:${password}`)}`;

  const [epics, setEpics] = useState<Epic[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingEpics, setLoadingEpics] = useState(false);
  const [loadingIssues, setLoadingIssues] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [taskCreationStatus, setTaskCreationStatus] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch epics and FreeAgent projects when component mounts
  useEffect(() => {
    setLoadingEpics(true);
    setLoadingProjects(true);

    axios.get(`${process.env.REACT_APP_API_URL}/jira-epics`, {
      headers: {
        'Authorization': authHeader
      }
    }).then((response) => {
      setEpics(response.data);
      setLoadingEpics(false);
    });

    axios.get(`${process.env.REACT_APP_API_URL}/freeagent-projects`, {
      headers: {
        'Authorization': authHeader
      }
    }).then((response) => {
      setProjects(response.data);
      setLoadingProjects(false);
    });
  }, [authHeader]);

  const fetchIssuesAndTasks = (epicKey: string) => {
    setLoadingIssues(true);
    setLoadingTasks(true);

    axios.get(`${process.env.REACT_APP_API_URL}/jira-epics/${epicKey}/issues`, {
      headers: {
        'Authorization': authHeader
      }
    }).then((response) => {
      setIssues(response.data);
      setLoadingIssues(false);
    });

    // Find the corresponding FreeAgent project
    const project = projects.find((project) => project.jiraKey === epicKey);

    if (project) {
      // Fetch child tasks of the selected project
      axios.get(`${process.env.REACT_APP_API_URL}/freeagent-projects/${project.id}/tasks`, {
        headers: {
          'Authorization': authHeader
        }
      }).then((response) => {
        setTasks(response.data);
        setLoadingTasks(false);
      });
    } else {
      setLoadingTasks(false);
    }
  };

  const createMissingTasksInFreeAgent = async () => {
    setCreatingTasks(true);
    setTaskCreationStatus([]); // Reset the task creation status
    setError(null); // Reset error message
    setShowModal(true); // Show modal

    // Map missingTaskKeys to an array of promises
    const taskCreationPromises = missingTaskKeys.map(async (key, index) => {
      // Add a delay to ensure requests are spaced 5 seconds apart
      await new Promise(resolve => setTimeout(resolve, index * 5000));

      const issue = issues.find((issue) => issue.key === key);
      if (issue && selectedProject) {
        const taskName = `[${issue.key}] ${issue.summary}`;

        // Get the URL of the selected project for FreeAgent
        const projectId = selectedProject.id;

        // Define the request payload based on the API requirements
        const taskData = {
          name: taskName,
          isBillable: false, // You need to provide the correct value
          billingRate: '85', // You need to provide the correct value
          billingPeriod: 'hour', // You need to provide the correct value
          status: 'Active', // You need to provide the correct value
          jiraKey: issue.key
        };

        // Make the API call to create the task in FreeAgent
        return axios.post(`${process.env.REACT_APP_API_URL}/freeagent-projects/${projectId}/tasks`, taskData, {
          headers: {
            'Authorization': authHeader
          }
        })
          .then((response) => {
            console.log('Task created in FreeAgent:', response.data);
            setTaskCreationStatus(prevStatus => [...prevStatus, `Creating task ${taskName}... Success!`]);
          })
          .catch((error) => {
            setTaskCreationStatus(prevStatus => [...prevStatus, `Creating task ${taskName}... Failed`]);
            console.error('Error creating task in FreeAgent:', error);
          });
      }
      return Promise.resolve(); // Resolve promise if no task to create
    });

    // Wait for all tasks to be created, and reload lists when done
    Promise.allSettled(taskCreationPromises).then(() => {
      if (selectedProject) {
        fetchIssuesAndTasks(selectedProject.jiraKey); // Reload lists
      }
      setCreatingTasks(false); // Reset loading state
      setShowModal(false); // Hide modal
    });
  };


  const selectedProject = issues.length > 0 && projects.find((project) => project.name.includes(issues[0].epic));
  const issueKeys = issues.map((issue) => issue.key);
  const taskKeys = tasks.map((task) => task.jiraKey);
  const missingIssueKeys = taskKeys.filter((key) => !issueKeys.includes(key));
  const missingTaskKeys = issueKeys.filter((key) => !taskKeys.includes(key));

  return (
    <div className="container mt-3">
      <h1>Select a Jira Epic</h1>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {loadingEpics || creatingTasks ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <EpicSelector epics={epics} onSelect={fetchIssuesAndTasks} />
      )}
      {!selectedProject || loadingIssues || loadingTasks || creatingTasks || issues.length === 0 ? null : (
        <button onClick={createMissingTasksInFreeAgent} className="btn btn-primary">
          Create Missing Tasks In FreeAgent
        </button>
      )}
      <div className="row">
        <div className="col-md-6">
          <h2>Jira</h2>
          <div className="card">
            <div className="card-body">
              {loadingIssues || creatingTasks ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : (
                <IssuesList issues={issues} missingKeys={missingTaskKeys} />
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <h2>FreeAgent</h2>
          <div className="card">
            <div className="card-body">
              {loadingTasks || creatingTasks ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : selectedProject ? (
                <div>
                  <h3>{selectedProject.name}</h3>
                  <TasksList tasks={tasks} missingKeys={missingIssueKeys} />
                </div>
              ) : (
                <p>No corresponding project was found in FreeAgent for the selected Jira epic.</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Task Creation Progress</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><em>(*this is going to be slow because FreeAgent's API rate limits us)</em></p>
          {taskCreationStatus.map((status, index) => (
            <p key={index}>{status}</p>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );


};

export default JiraFreeAgentSyncPage;
